import type Konva from 'konva';
import { useEffect, useRef, useState } from 'react';
import {
  Circle,
  Ellipse,
  Group,
  Line,
  Rect,
  RegularPolygon,
  Star,
  Transformer,
} from 'react-konva';

import type { ShapeElement } from './useTextEditorStore';
import { useTextEditorStore } from './useTextEditorStore';

/** Parse a hex color string into [r, g, b] components (0-255 each).
 * Returns [0, 0, 0] for non-hex inputs (e.g. rgba/hsl/named colors). */
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(clean)) {
    return [0, 0, 0];
  }
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const num = parseInt(full.slice(0, 6), 16);
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
}

/** Convert a fill value to Konva fill props for a solid color. */
function resolveSolidFill(fill: ShapeElement['fill']): string {
  return typeof fill === 'string' ? fill : fill.colors[0];
}

/**
 * Compute Konva linear-gradient props from a fill descriptor.
 * Angle 0 = top→bottom, 90 = left→right (matches CSS convention).
 */
function linearGradientProps(
  fill: Extract<ShapeElement['fill'], { type: 'linear' }>,
  width: number,
  height: number,
) {
  const angleRad = ((fill.angle ?? 90) * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  // Compute start/end points along the diagonal of the bounding box
  const halfW = width / 2;
  const halfH = height / 2;
  return {
    fillLinearGradientStartPoint: { x: -halfW * sin + halfW, y: -halfH * cos + halfH },
    fillLinearGradientEndPoint: { x: halfW * sin + halfW, y: halfH * cos + halfH },
    fillLinearGradientColorStops: [0, fill.colors[0], 1, fill.colors[1]] as (string | number)[],
  };
}

/**
 * Compute Konva radial-gradient props from a fill descriptor.
 * Center-to-edge gradient (inner radius = 0, outer = half the min dimension).
 */
function radialGradientProps(
  fill: Extract<ShapeElement['fill'], { type: 'radial' }>,
  width: number,
  height: number,
) {
  const cx = width / 2;
  const cy = height / 2;
  const r = Math.min(width, height) / 2;
  return {
    fillRadialGradientStartPoint: { x: cx, y: cy },
    fillRadialGradientStartRadius: 0,
    fillRadialGradientEndPoint: { x: cx, y: cy },
    fillRadialGradientEndRadius: r,
    fillRadialGradientColorStops: [0, fill.colors[0], 1, fill.colors[1]] as (string | number)[],
  };
}

/**
 * Build a feather overlay radial gradient that fades the shape edges to
 * transparent. The feather value (0–100) controls how far inward the fade
 * starts: 0 = no fade, 100 = fade from the very center.
 *
 * Returns Konva radial-gradient fill props that overlay on top of the shape
 * fill, going from transparent center to transparent (with the opaque band
 * starting at `1 - feather/100` of the radius).
 */
function featherFillProps(
  solidColor: string,
  feather: number,
  width: number,
  height: number,
): Record<string, unknown> {
  // Parse base fill color for the opaque zone
  const [r, g, b] = hexToRgb(solidColor);
  const opaqueColor = `rgba(${r},${g},${b},1)`;
  const transparentColor = `rgba(${r},${g},${b},0)`;

  const cx = width / 2;
  const cy = height / 2;
  const outerR = Math.sqrt(cx * cx + cy * cy); // corner-to-center = full coverage
  // Inner radius is the opaque zone boundary; feather=100 → 0, feather=0 → outerR
  const innerR = outerR * (1 - feather / 100);

  return {
    fillRadialGradientStartPoint: { x: cx, y: cy },
    fillRadialGradientStartRadius: innerR,
    fillRadialGradientEndPoint: { x: cx, y: cy },
    fillRadialGradientEndRadius: outerR,
    fillRadialGradientColorStops: [0, opaqueColor, 1, transparentColor] as (string | number)[],
  };
}

interface KonvaShapeNodeProps {
  element: ShapeElement;
  isSelected: boolean;
  onDragMove?: (
    id: string,
    x: number,
    y: number,
    w: number,
    h: number,
    node: { x: (v: number) => void; y: (v: number) => void },
  ) => void;
  onDragEndGuides?: () => void;
}

export function KonvaShapeNode({
  element,
  isSelected,
  onDragMove,
  onDragEndGuides,
}: KonvaShapeNodeProps) {
  const groupRef = useRef<Konva.Group>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const [dims, setDims] = useState({ w: element.width, h: element.height });
  const [isResizing, setIsResizing] = useState(false);

  const updateElement = useTextEditorStore((s) => s.updateElement);
  const selectElement = useTextEditorStore((s) => s.selectElement);

  // Sync dims when element size changes externally (e.g. undo/redo)
  useEffect(() => {
    if (!isResizing) {
      setDims({ w: element.width, h: element.height });
    }
  }, [element.width, element.height, isResizing]);

  // Attach transformer to the group when selected
  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current]);
      trRef.current.forceUpdate();
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, dims]);

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    updateElement(element.id, {
      x: e.target.x(),
      y: e.target.y(),
    });
    onDragEndGuides?.();
  };

  const handleTransform = () => {
    const node = groupRef.current;
    if (!node) return;
    const sx = node.scaleX();
    const sy = node.scaleY();
    const newW = Math.max(10, node.width() * sx);
    const newH = Math.max(10, node.height() * sy);
    node.width(newW);
    node.height(newH);
    node.scaleX(1);
    node.scaleY(1);
    setIsResizing(true);
    setDims({ w: newW, h: newH });
  };

  const handleTransformEnd = () => {
    const node = groupRef.current;
    if (!node) return;
    const newW = Math.max(10, node.width());
    const newH = Math.max(10, node.height());
    updateElement(element.id, {
      x: node.x(),
      y: node.y(),
      width: newW,
      height: newH,
      rotation: node.rotation(),
    });
    setIsResizing(false);
    setDims({ w: newW, h: newH });
  };

  const w = dims.w;
  const h = dims.h;
  const opacity = element.opacity / 100;
  const fill = element.fill;
  const feather = element.feather ?? 0;

  // Build fill props for the shape primitive
  const fillProps: Record<string, unknown> = {};
  if (feather > 0) {
    // Feather: radial gradient from solid center → transparent edge.
    // Use the first/only color as the solid base.
    const baseColor = resolveSolidFill(fill);
    Object.assign(fillProps, featherFillProps(baseColor, feather, w, h));
  } else if (typeof fill === 'string') {
    fillProps.fill = fill;
  } else if (fill.type === 'linear') {
    Object.assign(fillProps, linearGradientProps(fill, w, h));
  } else {
    // radial
    Object.assign(fillProps, radialGradientProps(fill, w, h));
  }

  // Shared non-fill props for all shape primitives
  const commonProps = {
    ...fillProps,
    opacity,
    perfectDrawEnabled: false,
    listening: true,
  };

  const renderShape = () => {
    switch (element.shapeType) {
      case 'rectangle': {
        const radiusFraction = (element.cornerRadius ?? 0) / 100;
        const cornerRadius = radiusFraction * (Math.min(w, h) / 2);
        return (
          <Rect
            x={0}
            y={0}
            width={w}
            height={h}
            cornerRadius={cornerRadius}
            {...commonProps}
          />
        );
      }

      case 'circle': {
        const radius = Math.min(w, h) / 2;
        return (
          <Circle
            x={w / 2}
            y={h / 2}
            radius={radius}
            {...commonProps}
          />
        );
      }

      case 'ellipse': {
        return (
          <Ellipse
            x={w / 2}
            y={h / 2}
            radiusX={w / 2}
            radiusY={h / 2}
            {...commonProps}
          />
        );
      }

      case 'star': {
        const outerRadius = Math.min(w, h) / 2;
        const numPoints = element.points ?? 5;
        const innerFraction = element.innerRadius ?? 0.5;
        const innerRadius = outerRadius * innerFraction;
        return (
          <Star
            x={w / 2}
            y={h / 2}
            numPoints={numPoints}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            {...commonProps}
          />
        );
      }

      case 'triangle': {
        return (
          <RegularPolygon
            x={w / 2}
            y={h / 2}
            sides={3}
            radius={Math.min(w, h) / 2}
            {...commonProps}
          />
        );
      }

      case 'line': {
        const strokeWidth = element.strokeWidth ?? 2;
        const strokeColor =
          typeof element.strokeColor === 'string' ? element.strokeColor : resolveSolidFill(fill);
        return (
          <Line
            points={[0, h / 2, w, h / 2]}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={opacity}
            perfectDrawEnabled={false}
            listening
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <Group
        ref={groupRef}
        x={element.x}
        y={element.y}
        width={w}
        height={h}
        rotation={element.rotation}
        draggable
        onClick={() => selectElement(element.id)}
        onTap={() => selectElement(element.id)}
        onDragMove={(e: Konva.KonvaEventObject<DragEvent>) => {
          const node = e.target;
          onDragMove?.(element.id, node.x(), node.y(), w, h, node);
        }}
        onDragEnd={handleDragEnd}
        onTransform={handleTransform}
        onTransformEnd={handleTransformEnd}
      >
        {renderShape()}
      </Group>

      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 10 || newBox.height < 10) {
              return oldBox;
            }
            return newBox;
          }}
          enabledAnchors={[
            'top-left',
            'top-center',
            'top-right',
            'middle-left',
            'middle-right',
            'bottom-left',
            'bottom-center',
            'bottom-right',
          ]}
          anchorSize={9}
          anchorCornerRadius={4}
          borderStroke="rgba(255,255,255,0.9)"
          borderStrokeWidth={1.5}
          anchorStroke="rgba(255,255,255,0.9)"
          anchorFill="rgba(30,30,30,0.85)"
          borderDash={[4, 4]}
          rotateAnchorOffset={28}
          ignoreStroke
        />
      )}
    </>
  );
}
